import { useRef, useState, useEffect } from "react";
import cx from "classnames";
import "./TabBar.scss";
import { Icon, IconName } from "../Icon";

type TabOption = {
  id: string;
  label: string;
  iconName?: string;
  disabled?: boolean;
};

type TabBarProps = {
  options: TabOption[];
  selected?: string;
  onChange?: (id: string) => void;
  className?: string;
  variant?: "lg" | "md" | "sm";
  disabled?: boolean;
};

const SCROLL_SPEED_MODIFIER = 1.5;

const TabBar: React.FC<TabBarProps> = ({
  options,
  selected,
  onChange,
  className,
  variant = "lg",
  disabled = false,
}) => {
  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    selected && options.some((option) => option.id === selected)
      ? selected
      : options.length > 0
        ? options[0].id
        : "",
  );

  const effectiveSelectedId =
    selected !== undefined ? selected : internalSelectedId;

  // Scrolling refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);
  const startX = useRef<number>(0);
  const scrollLeft = useRef<number>(0);

  // Gradient indicators
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  // Track if tabs are overflowing
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      setIsOverflowing(hasOverflow);
      return hasOverflow;
    }
    return false;
  };

  // Monitor scroll position to show/hide gradients
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      const maxScrollLeft = scrollWidth - clientWidth;

      setShowLeftGradient(scrollLeft > 10);
      setShowRightGradient(scrollLeft < maxScrollLeft - 10);
    }
  };

  useEffect(() => {
    handleScroll();

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const hasOverflow = checkOverflow();
      setShowRightGradient(hasOverflow);
    }
  }, [options]);

  useEffect(() => {
    const handleResize = () => {
      checkOverflow();
      handleScroll();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current) {
      isDragging.current = true;
      startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
      scrollLeft.current = scrollContainerRef.current.scrollLeft;
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollContainerRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX.current) * SCROLL_SPEED_MODIFIER;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  const handleTabClick = (id: string) => {
    if (selected === undefined) {
      setInternalSelectedId(id);
    }
    onChange?.(id);

    // Scroll the selected tab into view if needed
    const selectedTab = document.getElementById(`tab-${id}`);
    if (selectedTab && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const tabRect = selectedTab.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      if (tabRect.left < containerRect.left) {
        container.scrollBy({
          left: tabRect.left - containerRect.left - 20,
          behavior: "smooth",
        });
      } else if (tabRect.right > containerRect.right) {
        container.scrollBy({
          left: tabRect.right - containerRect.right + 20,
          behavior: "smooth",
        });
      }
    }
  };

  if (variant === "lg") {
    return (
      <div className={cx("TabBar", className)}>
        <div
          className={cx("TabBar__Gradient TabBar__Gradient--Left", {
            "TabBar__Gradient--Show":
              showLeftGradient && effectiveSelectedId !== options[0].id,
          })}
        />

        <div
          className={cx("TabBar__ScrollContainer", {
            "TabBar__ScrollContainer--Overflowing": isOverflowing,
          })}
          ref={scrollContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {options.map((option) => (
            <button
              id={`tab-${option.id}`}
              key={option.id}
              className={cx("TabBar__Tab", {
                "TabBar__Tab--Selected": option.id === effectiveSelectedId,
                "TabBar__Tab--Disabled": disabled || option?.disabled,
              })}
              onClick={() => handleTabClick(option.id)}
              type="button"
              disabled={disabled || option?.disabled}
            >
              {option.iconName && (
                <Icon iconName={option.iconName as IconName} size={16} />
              )}
              {option.label ? option.label : ""}
            </button>
          ))}
        </div>

        <div
          className={cx("TabBar__Gradient TabBar__Gradient--Right", {
            "TabBar__Gradient--Show":
              showRightGradient &&
              effectiveSelectedId !== options[options.length - 1].id,
          })}
        />
      </div>
    );
  } else if (variant === "md") {
    return (
      <div className={cx("TabBarMedium", className)}>
        {options.map((option) => (
          <button
            id={`tab-${option.id}`}
            key={option.id}
            className={cx("TabBarMedium__Tab", {
              "TabBarMedium__Tab--Selected": option.id === effectiveSelectedId,
              "TabBarMedium__Tab--Disabled": disabled || option?.disabled,
            })}
            onClick={() => handleTabClick(option.id)}
            type="button"
            disabled={disabled || option?.disabled}
          >
            {option.iconName && (
              <Icon iconName={option.iconName as IconName} size={16} />
            )}
            {option.label ? option.label : ""}
          </button>
        ))}
      </div>
    );
  } else {
    return (
      <div className={cx("TabBarSmall", className)}>
        {options.map((option) => (
          <button
            id={`tab-${option.id}`}
            key={option.id}
            className={cx("TabBarSmall__Tab", {
              "TabBarSmall__Tab--Selected": option.id === effectiveSelectedId,
              "TabBarSmall__Tab--Disabled": disabled || option?.disabled,
            })}
            onClick={() => handleTabClick(option.id)}
            type="button"
            disabled={disabled || option?.disabled}
          >
            {option.iconName && (
              <Icon iconName={option.iconName as IconName} size={16} />
            )}
            {option.label ? option.label : ""}
          </button>
        ))}
      </div>
    );
  }
};

export { TabBar };
